import is from '@sindresorhus/is';
import { useMemo } from 'react';

import { theme } from '../../theme/cobrainer-theme';
import type { TileKind, TileWidthStyle } from './types';

type TileDef = {
    readonly id: string;
    readonly minWidth?: string;
    readonly maxWidth?: string;
    readonly width?: string;
    readonly kind?: TileKind;
};

const DEFAULT_SIDEPANEL_WIDTH = '50%';

const isCollapsedDock = (tile: TileDef): boolean =>
    tile.kind === 'dock' && (tile.width === '0px' || tile.width === '0' || is.undefined(tile.width));

const resolveSidepanelWidth = (tile: TileDef, isLargeScreen: boolean, isFirstSidepanel: boolean): string => {
    if (tile.kind === 'dock') {
        return tile.width ?? '0px';
    }

    if (isLargeScreen && isFirstSidepanel) {
        if (is.nonEmptyString(tile.maxWidth) && tile.maxWidth === tile.minWidth) {
            return tile.maxWidth;
        }

        if (is.nonEmptyString(tile.maxWidth)) {
            return `min(${DEFAULT_SIDEPANEL_WIDTH}, ${tile.maxWidth})`;
        }

        return DEFAULT_SIDEPANEL_WIDTH;
    }

    if (isLargeScreen) {
        return tile.maxWidth ?? DEFAULT_SIDEPANEL_WIDTH;
    }

    return tile.minWidth ?? DEFAULT_SIDEPANEL_WIDTH;
};

const buildMainContentWidth = (gapSize: string, contributingWidths: readonly string[]): string => {
    if (contributingWidths.length === 0) {
        return '100%';
    }

    const gaps = contributingWidths.map(() => gapSize);
    const subtractions = [...gaps, ...contributingWidths];

    return `calc(100% - ${subtractions.join(' - ')})`;
};

const getTileWidthStyle = (tile: TileDef, width: string, gapSize: string, isFirstPanel: boolean): TileWidthStyle => {
    if (isFirstPanel) {
        return {
            maxWidth: is.nonEmptyString(tile.maxWidth) ? `calc(${tile.maxWidth} - ${gapSize})` : undefined,
            minWidth: is.nonEmptyString(tile.minWidth) ? `calc(${tile.minWidth} - ${gapSize})` : undefined,
            width: `calc(${width} - ${gapSize})`,
        };
    }

    return {
        maxWidth: tile.maxWidth,
        minWidth: tile.minWidth,
        width,
    };
};

const getDefaultMinWidth = (isLargeScreen: boolean, isMainContent: boolean): string | undefined => {
    if (!isMainContent) {
        return undefined;
    }

    return isLargeScreen ? '50%' : '400px';
};

export const useTileWidths = (
    tiles: readonly TileDef[],
    isLargeScreen: boolean,
): ReadonlyMap<string, TileWidthStyle> => {
    return useMemo(() => {
        if (tiles.length === 1) {
            const [tile] = tiles;
            if (tile === undefined) {
                return new Map<string, TileWidthStyle>();
            }

            return new Map<string, TileWidthStyle>([
                [
                    tile.id,
                    {
                        maxWidth: tile.maxWidth,
                        minWidth: tile.minWidth,
                        width: '100%',
                    },
                ],
            ]);
        }

        const gapSize = theme.spacing.m;

        // A collapsed dock (width === '0px') participates as a tile slot so
        // children can portal into it, but it must NOT contribute to the
        // main width subtraction (no width, no gap). When the dock animates
        // open, it re-enters the contribution list and the main width
        // shrinks.
        const sidepanelTiles = tiles.slice(1);
        const contributingTiles = sidepanelTiles.filter((tile) => !isCollapsedDock(tile));
        const contributingWidths = contributingTiles.map((tile, index) =>
            resolveSidepanelWidth(tile, isLargeScreen, index === 0),
        );
        const mainContentWidth = buildMainContentWidth(gapSize, contributingWidths);

        const result = new Map<string, TileWidthStyle>();

        for (const [index, tile] of tiles.entries()) {
            const isMainContent = index === 0;

            if (isMainContent) {
                result.set(tile.id, {
                    maxWidth: tile.maxWidth,
                    minWidth: tile.minWidth ?? getDefaultMinWidth(isLargeScreen, isMainContent),
                    width: mainContentWidth,
                });
                continue;
            }

            if (tile.kind === 'dock') {
                result.set(tile.id, {
                    maxWidth: tile.maxWidth,
                    minWidth: tile.minWidth,
                    width: tile.width ?? '0px',
                });
                continue;
            }

            const contributingIndex = contributingTiles.indexOf(tile);
            const isFirstPanel = contributingIndex === 0;
            const sidepanelWidth = isLargeScreen
                ? (contributingWidths[contributingIndex] ?? '50%')
                : (tile.minWidth ?? '50%');

            result.set(tile.id, getTileWidthStyle(tile, sidepanelWidth, gapSize, isFirstPanel));
        }

        return result;
    }, [tiles, isLargeScreen]);
};
